import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, signal } from '@angular/core';
import { AinDataColumn, AinDataTableComponent } from '../../shared/design-system/data-table.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { CurrentSubscription, SubscriptionPlan, SubscriptionService } from './subscription.service';

@Component({
  standalone: true,
  selector: 'ain-subscription',
  imports: [AinDataTableComponent, AinPageComponent, AinStateComponent],
  template: `
    <ain-page [eyebrow]="t('subscription.eyebrow')" [title]="t('subscription.title')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('subscription.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('subscription.unavailable')" [message]="error() ?? ''" />
      } @else if (currentSubscription()) {
        <article>
          <h3>{{ currentSubscription()?.planCode }}</h3>
          <p>{{ t('subscription.status') }}: {{ currentSubscription()?.status }}</p>
          <p>{{ t('subscription.users') }}: {{ currentSubscription()?.selectedUserCount }}</p>
          <p>{{ t('subscription.companies') }}: {{ currentSubscription()?.selectedCompanyCount }}</p>
          <strong>{{ currentSubscription()?.finalPrice }}</strong>
        </article>
      } @else {
        <ain-state type="empty" [title]="t('subscription.emptyTitle')" [message]="t('subscription.emptyMessage')" />
      }

      <ain-data-table title="subscription.availablePlans" [items]="plans()" [columns]="planColumns" />
    </ain-page>
  `,
  styles: [`
    article { border: 1px solid #e5e7eb; border-radius: 14px; padding: 16px; background: var(--ain-color-surface); }
  `]
})
export class SubscriptionComponent implements OnInit {
  private readonly subscriptionService = inject(SubscriptionService);
  private readonly localizationService = inject(LocalizationService);

  readonly currentSubscription = signal<CurrentSubscription | null>(null);
  readonly plans = signal<SubscriptionPlan[]>([]);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly planColumns: AinDataColumn<SubscriptionPlan>[] = [
    { field: 'name', header: 'columns.name', sortable: true },
    { field: 'code', header: 'columns.code', sortable: true },
    { field: 'billingCycle', header: 'columns.cycle' },
    { field: 'basePrice', header: 'columns.basePrice', sortable: true },
    { field: 'currency', header: 'columns.currency' }
  ];

  ngOnInit(): void {
    this.subscriptionService.listPlans().subscribe({
      next: plans => this.plans.set(plans),
      error: () => this.error.set(this.t('subscription.errorPlans'))
    });

    this.subscriptionService.getCurrentSubscription().subscribe({
      next: subscription => {
        this.currentSubscription.set(subscription);
        this.loading.set(false);
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 404) {
          this.currentSubscription.set(null);
          this.loading.set(false);
          return;
        }

        this.error.set(this.t('subscription.errorCurrent'));
        this.loading.set(false);
      }
    });
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }
}
